/**
 * The browser-side half of `/tickets` (WP 8.6) — same auth shape as
 * `linkPreview.ts`'s `fetchLinkPreview`: the Supabase session, not
 * `?key=ARCHIVE_KEY`. See `src/inbox.ts`'s `ticketStatusRoute` for why.
 *
 * Never automatic. `useTicketCheck` is a `useQuery` with `enabled: false`:
 * the query exists on mount (so its cache, `isFetching` and error state are
 * all there for the sheet to read the moment it opens) but nothing runs
 * until `refetch()` is called from a tap (the sheet's *Check*, or *Check
 * tickets* in its menu — WP 10.2) — no check fires
 * on opening the sheet, per the brief. Because the query key is stable
 * (`['ticket_check', entryId]`), re-opening the same sheet later in the same
 * session shows whatever the last tap found, immediately, before any new tap —
 * TanStack Query's own cache does that for free, the same way `useFeedDetail`
 * shows a stale answer while its own refetch is still in flight.
 *
 * WP 10.7: the wording and every pure shape (`TicketStatus`, `DateTicket`,
 * `dateTicket`, `ticketLine`, `DATE_MARK`, `WORD`, `anyTicketFact`, …) moved
 * to `./ticketWords`, a no-import file, so they can be reached by the
 * offline suite — this file imports the Supabase client and never could be.
 * Re-exported below so nothing importing from here had to change.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'
import { asStatus, isKnown, showingKey, type ShowingTicket, type TicketCheckResult } from './ticketWords'

export {
  DATE_MARK, WORD, anyTicketFact, dateTicket, isSoldOut, reasonLine, showingKey, ticketLine,
  type DateTicket, type Known, type ShowingTicket, type TicketCheckResult, type TicketReason, type TicketStatus,
} from './ticketWords'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

export async function checkTickets(entryId: string): Promise<TicketCheckResult> {
  if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not signed in')

  const res = await fetch(`${WORKER_URL}/tickets?id=${encodeURIComponent(entryId)}`, {
    headers: { authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`Ticket check failed: ${res.status} ${await res.text()}`)
  return res.json()
}

export function useTicketCheck(entryId: string | null) {
  const qc = useQueryClient()
  return useQuery<TicketCheckResult>({
    queryKey: ['ticket_check', entryId],
    queryFn: async () => {
      const result = await checkTickets(entryId as string)
      // WP 10.4's tap writes per-date rows into `showings`; read them back
      // rather than trusting that this response and the table agree.
      void qc.invalidateQueries({ queryKey: ['showing_tickets'] })
      return result
    },
    enabled: false,
    staleTime: Infinity,
  })
}

/**
 * The per-date statuses for one card's entries — only the showings that have
 * a known one, so a sheet with nothing known costs one empty GET.
 *
 * Its own small query rather than four more columns on `useShowings`, which
 * every screen reads 2,700 rows of: only the open sheet wants these, and
 * only for the one card it is showing.
 */
export function useShowingTickets(ids: string[]) {
  const { session } = useAuth()
  const key = ids.join(',')
  return useQuery<ShowingTicket[]>({
    queryKey: ['showing_tickets', key],
    enabled: !!session && ids.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('showing_list')
        .select('entry_id,starts_at,ticket_status,ticket_checked_at,ticket_source')
        .in('entry_id', key.split(','))
        .not('ticket_status', 'is', null)
        .gte('starts_at', new Date().toISOString())
      if (error) throw new Error(error.message)
      return (data ?? []).flatMap(r => {
        const status = asStatus(r.ticket_status)
        return isKnown(status) && r.entry_id && r.starts_at
          ? [{ entry_id: r.entry_id, starts_at: r.starts_at, status, checked_at: r.ticket_checked_at, source: r.ticket_source }]
          : []
      })
    },
  })
}

const NO_SOLD_OUT: ReadonlySet<string> = new Set()

/**
 * Every future showing marked sold out, as `showingKey`s — what the lists
 * hide a card by (a1-27, `isSoldOut`). Empty until it arrives, and on an
 * error: a list that cannot learn what is sold out shows everything, which
 * is what it did before.
 *
 * Its own small query rather than a column on `showing_page`: the marks are
 * on ~1% of showings (72 of ~9,200 on 2 Oct), so the 72 rows cost less than
 * a tenth column on every one, and no migration has to ship ahead of the
 * client. Under the `showing_tickets` key so a ticket check's invalidation
 * (`useTicketCheck`) refreshes it too. A query, not `fetchAll`: should the
 * marks pass PostgREST's 1,000-row cap, the rows beyond it stay shown, the
 * safe side of this rule.
 */
export function useSoldOut(): ReadonlySet<string> {
  const { session } = useAuth()
  const q = useQuery<ReadonlySet<string>>({
    queryKey: ['showing_tickets', 'sold_out'],
    enabled: !!session,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('showing_list')
        .select('entry_id,starts_at')
        .eq('ticket_status', 'sold_out')
        .gte('starts_at', new Date().toISOString())
      if (error) throw new Error(error.message)
      return new Set((data ?? []).flatMap(r => (r.entry_id && r.starts_at ? [showingKey(r.entry_id, r.starts_at)] : [])))
    },
  })
  return q.data ?? NO_SOLD_OUT
}
